import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    signInWithPopup, 
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    setPersistence,
    browserLocalPersistence
} from "firebase/auth";
import { auth, googleProvider, db } from "./firebase.js";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
 
/**
 * Sign up a new user with email and password
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<User>}
 */
export async function signUpWithEmail(email, password) {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        return userCredential.user;
    } catch (error) {
        console.error("Error signing up with email:", error.code, error.message);
        throw error;
    }
}

/**
 * Sends a password reset email to the user.
 * @param {string} email 
 */
export async function resetPassword(email) {
    try {
        await sendPasswordResetEmail(auth, email);
    } catch (error) {
        console.error("Error sending password reset email:", error);
        throw error;
    }
}

/**
 * Sends an email verification to the user.
 * @param {User} user 
 */
export async function sendVerification(user) {
    try {
        await sendEmailVerification(user);
    } catch (error) {
        console.error("Error sending verification email:", error);
        throw error;
    }
}

/**
 * Sign in an existing user with email and password
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<User>}
 */
export async function signInWithEmail(email, password) {
    try {
        await setPersistence(auth, browserLocalPersistence);
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return userCredential.user;
    } catch (error) {
        console.error("Error signing in with email:", error.code, error.message);
        throw error;
    }
}

/**
 * Sign in with Google (Popup)
 * @returns {Promise<User>}
 */
export async function signInWithGoogle() {
    try {
        await setPersistence(auth, browserLocalPersistence);
        const result = await signInWithPopup(auth, googleProvider);
        return result.user;
    } catch (error) {
        console.error("Error signing in with Google:", error.code, error.message);
        throw error;
    }
}

/**
 * Saves user preferences to a 'users' collection in Firestore.
 * @param {string} userId The UID of the user.
 * @param {object} preferences An object containing user preferences.
 * @returns {Promise<void>}
 */
export async function saveUserPreferences(userId, preferences) {
    if (!userId) throw new Error("User ID is required to save preferences.");
    try {
        // Creates a document reference to /users/{userId}
        const userDocRef = doc(db, "users", userId);
        // Fix: Use userDocRef, inject serverTimestamp
        await setDoc(userDocRef, { ...preferences, updatedAt: serverTimestamp() }, { merge: true });
    } catch (error) {
        console.error("Error saving user preferences to Firestore:", error);
        throw error;
    }
}

/**
 * Sign out the current user
 */
export async function logOut() {
    try {
        await signOut(auth);
    } catch (error) {
        console.error("Error signing out:", error);
        throw error;
    }
}